// Rendering rider-facing operator policy.
//
// Replaces the old metro-terms.ts, which chose between a hardcoded BMRCL block and three
// generic bullets based on `passes.some(p => p.qrDynamic)` — QR *format* standing in for
// operator *identity*. A Kochi rider therefore read Bangalore's cancellation window.
//
// Everything here now comes from the backend's operator registry. This module only decides
// HOW to render it, never WHAT is true.

import type { OperatorPolicyResponse } from './operator-policy-api';
import type { OperatorView, TermView } from './types';

/**
 * Localized templates per term code.
 *
 * The web app has no i18n, so this map exists only to phrase a few terms better than the
 * server's generic English — it is NOT a required translation table. Any code missing here
 * renders its server-provided `fallback`, so the backend can ship a brand-new term (or a
 * whole new city) with no web release at all.
 *
 * That fallback is also why a term is never silently dropped: quietly omitting a clause
 * with legal weight because this build predates its code would be a compliance problem,
 * not a cosmetic one.
 */
const TERM_TEMPLATES: Record<string, (t: TermView, o: OperatorView) => string> = {
  OPERATING_HOURS: (_t, o) =>
    `${o.brand ?? o.agency ?? 'This operator'} runs ${formatTime(o.serviceStart)}–${formatTime(o.serviceEnd)}${
      wrapsPastMidnight(o.serviceStart, o.serviceEnd) ? ' (the following day)' : ''
    }. Tickets are valid within these hours.`,

  CANCEL_WINDOW: (_t, o) =>
    `Cancellation is accepted within ${o.cancelWindowMins} minutes of booking.`,

  REFUND_ETA: (_t, o) =>
    `Refunds are credited to your original payment method within ${o.refundEtaMinDays}–${o.refundEtaMaxDays} working days.`,

  GROUP_ONE_QR: (_t, o) =>
    `You can book up to ${o.maxPassengers} tickets at once — the whole group travels on a single QR code.`,
};

/**
 * The rider terms to show, as ready-to-render strings.
 *
 * Returns [] when the operator block is absent (an older backend) or carries no terms —
 * the caller then hides the section entirely rather than inventing content.
 */
export function renderTerms(operator?: OperatorView): string[] {
  if (!operator?.terms?.length) return [];
  return operator.terms.map((term) => {
    const template = TERM_TEMPLATES[term.code];
    if (!template) return term.fallback;
    try {
      return template(term, operator);
    } catch {
      // A template that trips over a missing fact must never blank the clause.
      return term.fallback;
    }
  });
}

/**
 * The cancellation note for the confirm dialog.
 *
 * Only states a window when this operator actually has one. The old copy hardcoded
 * "(BMRCL: within 60 minutes of booking)" for every operator on the network.
 */
export function cancellationNote(operator?: OperatorView): string {
  const used =
    "Don't use your ticket after requesting cancellation — a used ticket can't be refunded.";
  if (operator?.cancelWindowMins) {
    return `Cancellation is accepted within ${operator.cancelWindowMins} minutes of booking. ${used}`;
  }
  return `Cancellation is subject to the operator's own window. ${used}`;
}

/** How long a refund takes, per this operator. Falls back to the platform-wide window. */
export function refundEta(operator?: OperatorView): string {
  const min = operator?.refundEtaMinDays ?? 3;
  const max = operator?.refundEtaMaxDays ?? 7;
  return `${min}–${max} working days`;
}

/**
 * The passenger cap, resolved in the order the backend itself uses: the operator's own
 * limit, then whatever the BPP's catalog advertised for this fare, then 6.
 *
 * The 6 is retained deliberately — it is the historical default, and dropping it would let
 * an operator that publishes no limit fall to 1.
 */
export function maxPassengers(
  operator: OperatorView | undefined,
  catalogMax: number | undefined
): number {
  return operator?.maxPassengers || catalogMax || 6;
}

/** Service-hours note for the booking screen, or '' when this operator's hours are unknown. */
export function serviceHoursNote(operator?: OperatorView): string {
  if (!operator?.serviceStart || !operator.serviceEnd) return '';
  const who = operator.brand ?? operator.agency ?? 'This operator';
  const suffix = wrapsPastMidnight(operator.serviceStart, operator.serviceEnd)
    ? ' the following day'
    : '';
  return `${who} runs ${formatTime(operator.serviceStart)}–${formatTime(operator.serviceEnd)}${suffix}. Tickets can only be bought during these hours, and are valid for same-day travel.`;
}

/** The operator's display name, preferring the consumer brand over the agency. */
export function operatorLabel(operator?: OperatorView): string {
  return operator?.brand || operator?.agency || '';
}

// "04:30" → "4:30 AM". Returns the raw value if it isn't HH:MM, so a malformed config
// degrades to something readable rather than "NaN:NaN".
function formatTime(hhmm?: string): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':');
  const hour = Number(h);
  if (!Number.isFinite(hour) || m === undefined) return hhmm;
  const suffix = hour < 12 ? 'AM' : 'PM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${suffix}`;
}

// BMRCL runs 04:30–02:30, i.e. the end time is on the next calendar day.
function wrapsPastMidnight(start?: string, end?: string): boolean {
  if (!start || !end) return false;
  return end < start;
}

/**
 * The policy that applies to a booking, mirroring the server's own resolution order:
 * the chosen operator's explicit entry, then the city default.
 *
 * Returns undefined when nothing matches, and callers must then render nothing rather
 * than reach for another operator's values.
 */
export function resolveOperator(
  policy: OperatorPolicyResponse | undefined,
  cityCode: string,
  bppId?: string
): OperatorView | undefined {
  if (!policy?.operators.length) return undefined;
  const inCity = policy.operators.filter(
    (o) => o.cityCode.toLowerCase() === cityCode.toLowerCase()
  );
  if (bppId) {
    const exact = inCity.find((o) => normalizeBppId(o.bppId) === normalizeBppId(bppId));
    if (exact) return exact;
  }
  // The city DEFAULT, or nothing. Never an arbitrary sibling: in a multi-operator city
  // (Mumbai's std:022 has four metro BPPs) falling through to the first entry would show a
  // rider on an unregistered BPP some other operator's window and cap — the exact
  // conflation this module was written to remove.
  return inCity.find((o) => !o.bppId);
}

// Mirrors the backend's beckn.NormalizeBppID: lowercase, trailing slashes trimmed.
function normalizeBppId(id?: string): string {
  return (id ?? '').trim().replace(/\/+$/, '').toLowerCase();
}
